import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sparkles } from "lucide-react";

const LINKS = [
  { label: "Solutions", href: "#solutions" },
  { label: "Why Us", href: "#why" },
  { label: "Industries", href: "#industries" },
  { label: "Resources", href: "#resources" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-0 inset-x-0 z-50 px-4 sm:px-6 lg:px-10 pt-4"
        data-testid="navbar"
      >
        <div
          className={`mx-auto max-w-7xl flex items-center justify-between rounded-full px-5 sm:px-6 py-3 transition-[background-color,box-shadow,border-color] duration-500 ${
            scrolled
              ? "bg-white/80 backdrop-blur-xl border border-[#EEF1F4] shadow-lg shadow-gray-200/60"
              : "bg-transparent border border-transparent"
          }`}
        >
          <a
            href="#top"
            data-testid="logo-link"
            className="flex items-center gap-2.5 group"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#374151] text-white">
              <Sparkles className="h-4 w-4" />
            </span>
            <span className="font-display font-bold text-lg tracking-tight text-[#1F2937]">
              SOBOT
              <span className="text-[#9CA3AF] font-medium"> Technologies</span>
            </span>
          </a>

          <nav className="hidden md:flex items-center gap-1">
            {LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`}
                className="relative px-4 py-2 text-sm font-medium text-[#6B7280] hover:text-[#1F2937] transition-colors duration-300"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="#contact"
              data-testid="nav-signin"
              className="text-sm font-medium text-[#6B7280] hover:text-[#1F2937] transition-colors duration-300"
            >
              Sign in
            </a>
            <motion.a
              href="#contact"
              data-testid="nav-cta"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="rounded-full bg-[#374151] px-5 py-2.5 text-sm font-semibold text-white shadow-sm"
            >
              Get Started
            </motion.a>
          </div>

          <button
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-open"
            className="md:hidden flex h-10 w-10 items-center justify-center rounded-full bg-[#F5F6F8] text-[#1F2937]"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-white p-6 md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between">
              <span className="font-display font-bold text-lg">SOBOT</span>
              <button
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F5F6F8]"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="mt-12 flex flex-col gap-2">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.label}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  className="font-display text-4xl font-bold tracking-tight text-[#1F2937] py-2"
                >
                  {l.label}
                </motion.a>
              ))}
            </nav>
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              className="mt-10 block rounded-full bg-[#374151] px-6 py-4 text-center font-semibold text-white"
            >
              Get Started
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
